import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Alert,
  CircularProgress,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Avatar,
  Fade,
  TextField,
  Grid,
  ToggleButton,
  ToggleButtonGroup,
  Divider,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import api from '../../utils/api';
import PageHeader from '../../components/Layout/PageHeader';
import { surfaceCardSx, tokens } from '../../theme/tokens';

const UPLOADS_BASE = (process.env.REACT_APP_API_BASE_URL || 'http://localhost:5001/api').replace(/\/api\/?$/, '') || 'http://localhost:5001';

function getPhotoUrl(displayPhoto, profilePicture) {
  const file = displayPhoto || profilePicture;
  if (!file) return null;
  return file.startsWith('http') ? file : `${UPLOADS_BASE}/uploads/${file}`;
}

function ExecutiveTransitionModal({ open, onClose, electionName, outgoing, incoming, onSeen }) {
  const [phase, setPhase] = useState('outgoing'); // 'outgoing' | 'incoming' | 'done'

  useEffect(() => {
    if (!open) return;
    setPhase('outgoing');
    const t = setTimeout(() => setPhase('incoming'), 3500);
    return () => clearTimeout(t);
  }, [open]);

  const handleContinue = async () => {
    await onSeen();
    onClose();
  };

  const flatOutgoing = Array.isArray(outgoing) ? outgoing : [];
  const flatIncoming = Array.isArray(incoming) ? incoming : [];

  return (
    <Dialog open={open} onClose={() => {}} maxWidth="md" fullWidth fullScreen sx={{ '& .MuiDialog-paper': { bgcolor: 'background.default' } }}>
      <DialogTitle sx={{ textAlign: 'center', pt: 3 }}>
        Change of Executives
        {electionName && (
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            {electionName}
          </Typography>
        )}
      </DialogTitle>
      <DialogContent sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 2 }}>
        {phase === 'outgoing' && (
          <Fade in={phase === 'outgoing'} timeout={600}>
            <Box>
              <Typography variant="subtitle1" color="text.secondary" gutterBottom>
                Outgoing executives
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, justifyContent: 'center', maxWidth: 560 }}>
                {flatOutgoing.map((ex) => (
                  <Box key={ex.id} sx={{ textAlign: 'center', minWidth: 100 }}>
                    <Avatar
                      src={getPhotoUrl(ex.display_photo, ex.profile_picture)}
                      sx={{ width: 72, height: 72, mx: 'auto', mb: 0.5 }}
                    >
                      {(ex.first_name?.[0] || '') + (ex.last_name?.[0] || '')}
                    </Avatar>
                    <Typography variant="body2">{ex.first_name} {ex.last_name}</Typography>
                    <Typography variant="caption" color="text.secondary">{ex.position_name}</Typography>
                  </Box>
                ))}
              </Box>
            </Box>
          </Fade>
        )}
        {phase === 'incoming' && (
          <Fade in={phase === 'incoming'} timeout={800}>
            <Box>
              <Typography variant="subtitle1" color="primary" gutterBottom>
                New executive team
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, justifyContent: 'center', maxWidth: 560 }}>
                {flatIncoming.map((ex) => (
                  <Box key={ex.id} sx={{ textAlign: 'center', minWidth: 100 }}>
                    <Avatar
                      src={getPhotoUrl(ex.display_photo, ex.profile_picture)}
                      sx={{ width: 72, height: 72, mx: 'auto', mb: 0.5, border: '2px solid', borderColor: 'primary.main' }}
                    >
                      {(ex.first_name?.[0] || '') + (ex.last_name?.[0] || '')}
                    </Avatar>
                    <Typography variant="body2">{ex.first_name} {ex.last_name}</Typography>
                    <Typography variant="caption" color="text.secondary">{ex.position_name}</Typography>
                  </Box>
                ))}
              </Box>
            </Box>
          </Fade>
        )}
      </DialogContent>
      <DialogActions sx={{ justifyContent: 'center', pb: 3 }}>
        <Button variant="contained" onClick={handleContinue} color="primary">
          Continue
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default function HRExecutives() {
  const [executives, setExecutives] = useState([]);
  const [positions, setPositions] = useState([]);
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [openDialog, setOpenDialog] = useState(false);
  /** @type {'select' | 'manual'} */
  const [assignMode, setAssignMode] = useState('select');
  const [formData, setFormData] = useState({ member_id: '', position_id: '' });
  const [manualForm, setManualForm] = useState({
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    gender: '',
    constituency: '',
  });
  const [termStart, setTermStart] = useState('');
  const [termEnd, setTermEnd] = useState('');
  const [duplicateMatches, setDuplicateMatches] = useState(null);
  const [forceDuplicateManual, setForceDuplicateManual] = useState(false);
  const [selectedDuplicateMemberId, setSelectedDuplicateMemberId] = useState('');
  const [duplicateHint, setDuplicateHint] = useState('');
  const [transitionData, setTransitionData] = useState(null);
  const [showTransition, setShowTransition] = useState(false);
  const [dialogError, setDialogError] = useState('');

  useEffect(() => {
    fetchExecutives();
    fetchPositions();
    fetchMembers();
    fetchTransitionStatus();
  }, []);

  const fetchExecutives = async () => {
    try {
      setLoading(true);
      setError('');
      const response = await api.get('/hr/executives');
      setExecutives(response.data.data || []);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to fetch executives');
    } finally {
      setLoading(false);
    }
  };

  const fetchTransitionStatus = async () => {
    try {
      const response = await api.get('/hr/executives/transition-status');
      const data = response.data?.data || {};
      if (data.show_transition && data.outgoing?.length > 0) {
        setTransitionData({
          election_id: data.election_id,
          election_name: data.election_name,
          outgoing: data.outgoing || [],
          incoming: data.incoming || [],
        });
        setShowTransition(true);
      }
    } catch {
      // ignore
    }
  };

  const handleTransitionSeen = async () => {
    if (!transitionData?.election_id) return;
    try {
      await api.post('/hr/executives/transition-seen', { election_id: transitionData.election_id });
      setTransitionData(null);
      setShowTransition(false);
    } catch {
      setShowTransition(false);
    }
  };

  const fetchPositions = async () => {
    try {
      const response = await api.get('/hr/positions', { params: { limit: 500, page: 1 } });
      setPositions(response.data.data || []);
    } catch (err) {
      console.error('Error fetching positions:', err);
    }
  };

  const fetchMembers = async () => {
    try {
      const response = await api.get('/members', { params: { limit: 5000, status: 'all' } });
      setMembers(response.data.data || []);
    } catch (err) {
      console.error('Error fetching members:', err);
    }
  };

  const handleOpenDialog = () => {
    setDialogError('');
    setAssignMode('select');
    setFormData({ member_id: '', position_id: '' });
    setManualForm({
      first_name: '',
      last_name: '',
      email: '',
      phone: '',
      gender: '',
      constituency: '',
    });
    setTermStart('');
    setTermEnd('');
    setDuplicateMatches(null);
    setForceDuplicateManual(false);
    setSelectedDuplicateMemberId('');
    setDuplicateHint('');
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setDialogError('');
    setAssignMode('select');
    setFormData({ member_id: '', position_id: '' });
    setManualForm({
      first_name: '',
      last_name: '',
      email: '',
      phone: '',
      gender: '',
      constituency: '',
    });
    setTermStart('');
    setTermEnd('');
    setDuplicateMatches(null);
    setForceDuplicateManual(false);
    setSelectedDuplicateMemberId('');
    setDuplicateHint('');
  };

  const handleManualChange = (e) => {
    const { name, value } = e.target;
    setManualForm((prev) => ({ ...prev, [name]: value }));
    setForceDuplicateManual(false);
    setDuplicateMatches(null);
    setSelectedDuplicateMemberId('');
  };

  const checkManualDuplicatesHint = async () => {
    if (assignMode !== 'manual') return;
    const fn = manualForm.first_name.trim();
    const ln = manualForm.last_name.trim();
    const em = manualForm.email.trim();
    const ph = manualForm.phone.trim();
    if (!fn || !ln || (!em && !ph)) {
      setDuplicateHint('');
      return;
    }
    try {
      const { data } = await api.get('/hr/executives/check-duplicate', {
        params: {
          first_name: fn,
          last_name: ln,
          email: em || undefined,
          phone: ph || undefined,
        },
      });
      const matches = data?.data?.matches || [];
      if (matches.length > 0) {
        setDuplicateHint(
          'Possible matches found in the directory. Consider selecting a registered member to avoid duplicates.'
        );
        setDuplicateMatches(matches);
      } else {
        setDuplicateHint('');
        setDuplicateMatches(null);
      }
    } catch {
      setDuplicateHint('');
    }
  };

  const handleUseRegisteredMember = (memberId) => {
    const id =
      memberId ||
      selectedDuplicateMemberId ||
      (duplicateMatches?.length === 1 ? duplicateMatches[0].id : '');
    if (!id) return;
    setAssignMode('select');
    setFormData((prev) => ({ ...prev, member_id: id }));
    setForceDuplicateManual(false);
    setDuplicateMatches(null);
    setSelectedDuplicateMemberId('');
    setDuplicateHint('');
    setDialogError('');
  };

  const handleSubmit = async (forceOverride) => {
    setError('');
    setDialogError('');
    const useForce = forceOverride === true || forceDuplicateManual;
    try {
      const posId = Number(formData.position_id);
      const base = {
        position_id: posId,
        term_start: termStart,
        term_end: termEnd,
      };
      if (assignMode === 'manual') {
        const payload = {
          ...base,
          mode: 'manual',
          first_name: manualForm.first_name.trim(),
          last_name: manualForm.last_name.trim(),
          email: manualForm.email.trim(),
          phone: manualForm.phone.trim(),
          gender: manualForm.gender,
          constituency: manualForm.constituency.trim() || undefined,
          force_duplicate_manual: useForce,
        };
        if (useForce && duplicateMatches && duplicateMatches.length > 1) {
          if (!selectedDuplicateMemberId) {
            setDialogError('Choose which member to assign, or switch to “Select registered member”.');
            return;
          }
          payload.existing_member_id = selectedDuplicateMemberId;
        }
        await api.post('/hr/executives', payload);
      } else {
        await api.post('/hr/executives', {
          ...base,
          mode: 'select',
          member_id: String(formData.member_id),
        });
      }
      handleCloseDialog();
      fetchExecutives();
      fetchMembers();
    } catch (err) {
      const status = err.response?.status;
      const body = err.response?.data;
      if (status === 409 && body?.code === 'DUPLICATE_MEMBER' && Array.isArray(body.matches)) {
        setDuplicateMatches(body.matches);
        setDuplicateHint(
          body.message ||
            'This person may already be registered. Select them from the list or assign to an existing record.'
        );
        setForceDuplicateManual(false);
        setSelectedDuplicateMemberId(body.matches.length === 1 ? body.matches[0].id : '');
        setDialogError('');
        return;
      }
      const msg =
        body?.message ||
        body?.error ||
        err.message ||
        'Failed to assign executive';
      console.error('Assign executive failed:', err.response?.status, err.response?.data);
      const show = typeof msg === 'string' ? msg : 'Failed to assign executive';
      setDialogError(show);
      setError(show);
    }
  };

  const canSubmit =
    !!formData.position_id &&
    !!termStart &&
    !!termEnd &&
    (assignMode === 'select'
      ? !!formData.member_id
      : !!(
          manualForm.first_name.trim() &&
          manualForm.last_name.trim() &&
          manualForm.email.trim() &&
          manualForm.phone.trim() &&
          manualForm.gender
        ));

  const handleDelete = async (executiveId) => {
    if (!window.confirm('Are you sure you want to remove this executive?')) {
      return;
    }
    try {
      await api.delete(`/hr/executives/${executiveId}`);
      fetchExecutives();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to remove executive');
    }
  };

  const formatTerm = (start, end) => {
    if (!start && !end) return '—';
    const s = start ? new Date(start).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' }) : '';
    const e = end ? new Date(end).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' }) : 'Present';
    return s ? `${s} – ${e}` : e;
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" p={4}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <ExecutiveTransitionModal
        open={showTransition && !!transitionData}
        onClose={() => setShowTransition(false)}
        electionName={transitionData?.election_name}
        outgoing={transitionData?.outgoing || []}
        incoming={transitionData?.incoming || []}
        onSeen={handleTransitionSeen}
      />

      <PageHeader
        crumbs={
          <>
            Registry / <Box component="b" sx={{ color: tokens.ink, fontWeight: 500 }}>Executives</Box>
          </>
        }
        title="Executives"
        actions={
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleOpenDialog}
            sx={{ bgcolor: tokens.primary, '&:hover': { bgcolor: tokens.primaryStrong } }}
          >
            Assign Executive
          </Button>
        }
      />

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {executives.length === 0 ? (
        <Paper elevation={0} sx={{ ...surfaceCardSx, p: 3 }}>
          <Typography variant="body2" color="text.secondary" align="center">
            There are no executives assigned. Executives are automatically added when an election completes, or you can assign manually.
          </Typography>
        </Paper>
      ) : (
        executives.map((group, index) => (
          <Accordion key={group.position.id} defaultExpanded={index === 0}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', width: '100%', mr: 2 }}>
                <Typography variant="h6">{group.position.name}</Typography>
                <Box sx={{ display: 'flex', gap: 2 }}>
                  <Chip label={`Total: ${group.statistics.total}`} size="small" />
                  <Chip label={`Male: ${group.statistics.male}`} size="small" color="primary" />
                  <Chip label={`Female: ${group.statistics.female}`} size="small" color="secondary" />
                </Box>
              </Box>
            </AccordionSummary>
            <AccordionDetails>
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Photo</TableCell>
                      <TableCell>Name</TableCell>
                      <TableCell>Email</TableCell>
                      <TableCell>Gender</TableCell>
                      <TableCell>Term</TableCell>
                      <TableCell align="right">Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {group.executives.map((exec) => (
                      <TableRow key={exec.id}>
                        <TableCell>
                          <Avatar
                            src={getPhotoUrl(exec.display_photo, exec.profile_picture)}
                            sx={{ width: 40, height: 40 }}
                          >
                            {exec.first_name?.[0]}{exec.last_name?.[0]}
                          </Avatar>
                        </TableCell>
                        <TableCell>
                          {exec.first_name} {exec.last_name}
                        </TableCell>
                        <TableCell>{exec.email}</TableCell>
                        <TableCell>{exec.gender}</TableCell>
                        <TableCell>{formatTerm(exec.term_start, exec.term_end)}</TableCell>
                        <TableCell align="right">
                          <Button
                            size="small"
                            color="error"
                            startIcon={<DeleteIcon />}
                            onClick={() => handleDelete(exec.id)}
                          >
                            Remove
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </AccordionDetails>
          </Accordion>
        ))
      )}

      <Dialog
        open={openDialog}
        onClose={handleCloseDialog}
        maxWidth="md"
        fullWidth
        disableRestoreFocus
        aria-labelledby="assign-executive-dialog-title"
        aria-describedby="assign-executive-dialog-description"
      >
        <DialogTitle id="assign-executive-dialog-title">Assign Executive</DialogTitle>
        <DialogContent id="assign-executive-dialog-description">
          {dialogError && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setDialogError('')}>
              {dialogError}
            </Alert>
          )}
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Choose an existing member or enter details to create a member record and assign the role.
          </Typography>
          <ToggleButtonGroup
            value={assignMode}
            exclusive
            fullWidth
            color="primary"
            sx={{ mb: 2 }}
            onChange={(e, v) => {
              if (!v) return;
              setAssignMode(v);
              setDuplicateMatches(null);
              setForceDuplicateManual(false);
              setSelectedDuplicateMemberId('');
              setDuplicateHint('');
            }}
          >
            <ToggleButton value="select">Select registered member</ToggleButton>
            <ToggleButton value="manual">Enter details manually</ToggleButton>
          </ToggleButtonGroup>

          <FormControl fullWidth sx={{ mb: 2 }}>
            <InputLabel>Position</InputLabel>
            <Select
              value={formData.position_id}
              onChange={(e) => setFormData({ ...formData, position_id: e.target.value })}
              label="Position"
              autoFocus
            >
              {positions.map((position) => (
                <MenuItem key={position.id} value={position.id}>
                  {position.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <Typography variant="subtitle2" sx={{ mb: 1 }}>
            Term of office (required)
          </Typography>
          <Grid container spacing={2} sx={{ mb: 2 }}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                required
                type="date"
                label="Term start"
                InputLabelProps={{ shrink: true }}
                value={termStart}
                onChange={(e) => setTermStart(e.target.value)}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                required
                type="date"
                label="Term end"
                InputLabelProps={{ shrink: true }}
                value={termEnd}
                onChange={(e) => setTermEnd(e.target.value)}
              />
            </Grid>
          </Grid>

          {assignMode === 'select' ? (
            <FormControl fullWidth>
              <InputLabel>Member</InputLabel>
              <Select
                value={formData.member_id}
                onChange={(e) => setFormData({ ...formData, member_id: e.target.value })}
                label="Member"
              >
                {members.map((member) => (
                  <MenuItem key={member.id} value={member.id}>
                    {member.first_name} {member.last_name} ({member.member_id})
                    {member.status && member.status !== 'Active' ? ` — ${member.status}` : ''}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          ) : (
            <>
              <Divider sx={{ my: 2 }} />
              <Typography variant="subtitle2" gutterBottom>
                New member (saved as Active, then assigned to this position)
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    required
                    label="First name"
                    name="first_name"
                    value={manualForm.first_name}
                    onChange={handleManualChange}
                    onBlur={checkManualDuplicatesHint}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    required
                    label="Last name"
                    name="last_name"
                    value={manualForm.last_name}
                    onChange={handleManualChange}
                    onBlur={checkManualDuplicatesHint}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    required
                    type="email"
                    label="Email"
                    name="email"
                    value={manualForm.email}
                    onChange={handleManualChange}
                    onBlur={checkManualDuplicatesHint}
                    autoComplete="off"
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    required
                    label="Phone"
                    name="phone"
                    value={manualForm.phone}
                    onChange={handleManualChange}
                    onBlur={checkManualDuplicatesHint}
                    helperText="10–15 digits"
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth required>
                    <InputLabel>Gender</InputLabel>
                    <Select
                      value={manualForm.gender}
                      label="Gender"
                      onChange={(e) =>
                        setManualForm((prev) => ({ ...prev, gender: e.target.value }))
                      }
                    >
                      <MenuItem value="Male">Male</MenuItem>
                      <MenuItem value="Female">Female</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Constituency (optional)"
                    name="constituency"
                    value={manualForm.constituency}
                    onChange={handleManualChange}
                    helperText="Used for member ID prefix if provided"
                  />
                </Grid>
              </Grid>
              {duplicateMatches && duplicateMatches.length > 0 && (
                <Alert severity="warning" sx={{ mt: 2 }}>
                  <Typography variant="body2" sx={{ mb: 1 }}>
                    {duplicateHint ||
                      'Possible matches found in the directory. Prefer “Select registered member”, or assign this role to an existing record without creating a duplicate profile.'}
                  </Typography>
                  {duplicateMatches.length > 1 && (
                    <FormControl fullWidth sx={{ mb: 2 }}>
                      <InputLabel>Which member?</InputLabel>
                      <Select
                        value={selectedDuplicateMemberId}
                        label="Which member?"
                        onChange={(e) => setSelectedDuplicateMemberId(e.target.value)}
                      >
                        {duplicateMatches.map((m) => (
                          <MenuItem key={m.id} value={m.id}>
                            {m.first_name} {m.last_name} — {m.email} ({m.member_id})
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  )}
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                    <Button variant="outlined" size="small" onClick={() => handleUseRegisteredMember()}>
                      Switch to registered member
                    </Button>
                    <Button
                      variant="contained"
                      color="secondary"
                      size="small"
                      disabled={!canSubmit}
                      onClick={() => handleSubmit(true)}
                    >
                      Assign to existing record (no new profile)
                    </Button>
                  </Box>
                </Alert>
              )}
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button onClick={() => handleSubmit()} variant="contained" disabled={!canSubmit}>
            Assign
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
